/**
 * TranscribingScreen (SK-CAPTURE, B3) — the record→upload→TRANSCRIBE leg.
 *
 * #32: PROGRESS-ONLY. Sits between ActiveCapture ("Stop & save") and SaveScreen.
 * Given the capture's voice part it: (1) durably uploads the audio blob to
 * /mark/media (idempotent by sha256 — the outbox's later commit-time PUT of the
 * same part is a safe no-op), (2) asks the coordinator to transcribe it, then
 * (3) hands the (possibly empty) transcript + the §9a.2 low-confidence flag
 * straight up to the caller and auto-advances to SaveScreen — where the
 * transcript is rendered EDITABLE. The old inline transcript-editor +
 * "Continue to save" interstitial that used to live here is GONE (Red:
 * "significant and out of bounds"); this screen never shows an editable field.
 *
 * Fail-soft by construction: the injected Transcriber degrades any error to an
 * empty transcript, so a failed/absent STT still advances the human to Save with
 * a blank editable field rather than a broken flow — transcription is a trust
 * aid, never a gate. A capture with no voice part skips straight through
 * (onDone with empty text).
 *
 * The indicator's PRESENCE is bound to the actual async lifecycle (a real pending
 * promise), never a timer — the honest signal, matching ActiveCapture's
 * onstart-bound REC pill. Finding D added a rotating word inside it; that
 * rotation IS on a timer, and the distinction is the point: the screen shows
 * because work is pending, and the word changes only so a stalled leg still looks
 * alive. No word claims progress, because nothing here knows any.
 */

import { useEffect, useState } from "preact/hooks";
import type { BlobRecord } from "../outbox/types";
import type { BlobPutScope, BlobPutter } from "../outbox/retry-unit";
import type { RoutingDetectionResult, Transcriber, TranscriptResult } from "../outbox/http-transport";
import { extFromMime } from "../outbox/media-role";

// #26: hard bound on the network leg. putBlob/transcribe use bare fetch with no
// timeout, so a stalled upload/transcribe would hang "Transcribing…" forever.
// 15s is generous for a mobile audio upload + transcribe; past it we degrade to
// an empty editable transcript so the human always reaches Save (the capture is
// already durably persisted at record-stop, #26 — nothing is lost).
const TRANSCRIBE_TIMEOUT_MS = 60000;

/**
 * Finding D (Red, 2026-07-28): "take a play from the Claude Code TUI — a rotating
 * list of construction/caveman terms" instead of one static line.
 *
 * WHAT THIS IS AND IS NOT. The rotation is LIVENESS, never progress. Nothing here
 * knows how far along the transcribe is, so no word may imply a fraction, a stage,
 * or a nearly-there — and the indicator's PRESENCE stays bound to the real pending
 * promise exactly as before. Only which word is showing runs on a clock. A super
 * staring at a frozen-looking phone on a bad connection needs to know the app is
 * still alive; that is the whole job.
 *
 * Every term describes what MARK is doing, never the human's input. "Grunting…"
 * was cut for that reason — the joke lands on the person who just spoke, and a
 * super who thinks the app is mocking their speech stops talking to it, which is
 * the one thing this product cannot survive (§1: adoption is THE risk).
 */
export const TRANSCRIBING_WORDS = [
  "Listening back…",
  "Chiseling…",
  "Blaming the weather…", // ← wry
  "Making marks…",
  "Deciphering…",
  "Whittling…",
  "Scratching it down…",
  "Digging through the truck…", // ← wry
  "Chipping away…",
  "Sharpening the pencil…",
  "Squinting at it…",
  "Dusting it off…",
  "Checking for plumb…", // ← wry
  "Wrangling words…",
  "Reading the scratchings…",
] as const;

/**
 * The wry ones (Red, 2026-07-29: "throw some tongue in cheek ones in there maybe
 * like 20% of the time"). Named rather than merely present so the RATIO is a
 * tested property instead of a thing that drifts the next time someone adds a
 * word: 3 of 15 is exactly 20%.
 *
 * WHY 20% FALLS OUT OF THE LIST rather than needing a dice roll. At small.en the
 * transcribe finishes in ~1–1.5s, so a human normally sees exactly ONE word — the
 * one `startIndex` seeds. That index is a hash of `capture_id` spread across the
 * list, so the share of captures that OPEN on a wry word is just wry/total. A
 * `Math.random() < 0.2` would buy the same frequency and cost the reproducibility
 * that lets a bug report be replayed.
 *
 * They are spread, not clustered, so a slow leg on a bad connection can never
 * show three gags back to back and start reading as a toy.
 *
 * SAME RULE AS THE REST, and it is the whole reason these three are safe: every
 * one describes MARK failing to get on with it. None is about the human or what
 * they just said. "Blaming the weather…" is Mark making an excuse for itself —
 * self-deprecating, which is the only direction the joke may point.
 */
export const TONGUE_IN_CHEEK: ReadonlySet<string> = new Set([
  "Blaming the weather…",
  "Digging through the truck…",
  "Checking for plumb…",
]);

/** One full pass through the list lands on the timeout — BY CONSTRUCTION, which
 * is why adding the wry entries did not have to touch it: the divisor is the list
 * length, so 12 words at 1250ms and 15 at 1000ms both come back around exactly as
 * the leg gives up. If the words have lapped, the leg is about to hand the human
 * an empty editable transcript; a list that lapped twice would say "still going"
 * about work that already died. Exported so the test asserts the RELATIONSHIP
 * rather than re-hardcoding a number that then has to be edited in two places. */
export const WORD_MS = Math.floor(TRANSCRIBE_TIMEOUT_MS / TRANSCRIBING_WORDS.length);

/** Where the rotation starts, derived from the capture so consecutive captures
 * do not all open on the same word. Deliberately NOT `Math.random()`: this is
 * still deterministic given a capture_id, so a test (or a bug report) can be
 * reproduced exactly. */
function startIndex(capture_id: string): number {
  let hash = 0;
  for (let i = 0; i < capture_id.length; i += 1) {
    hash = (hash * 31 + capture_id.charCodeAt(i)) >>> 0;
  }
  return hash % TRANSCRIBING_WORDS.length;
}

export interface TranscribingScreenProps {
  capture_id: string;
  /** The capture's voice part, or null when the capture has no audio (photo-
   * only) — in which case this screen resolves immediately with no transcript. */
  audioPart: BlobRecord | null;
  scope: BlobPutScope;
  putBlob: BlobPutter["putBlob"];
  transcriber: Transcriber;
  /** Default-off BUI-295 gate for the optional inferred review leg. */
  inferredRoutingSuggestionsEnabled?: boolean;
  /** Handoff to SaveScreen: the (possibly empty, possibly edited-later)
   * transcript + whether STT flagged it low-confidence. */
  onDone: (
    transcript: string,
    low_confidence: boolean,
    review_token?: string,
    routing_detection?: RoutingDetectionResult,
  ) => void;
}

export function TranscribingScreen(props: TranscribingScreenProps) {
  const {
    capture_id, audioPart, scope, putBlob, transcriber, onDone,
    inferredRoutingSuggestionsEnabled = false,
  } = props;
  const [wordIndex, setWordIndex] = useState(() => startIndex(capture_id));

  // Finding D: the word rotates on its own clock, independent of the leg below.
  // It is torn down with the screen, so it cannot outlive the work it describes.
  useEffect(() => {
    setWordIndex(startIndex(capture_id));
    const timer = setInterval(
      () => setWordIndex((i) => (i + 1) % TRANSCRIBING_WORDS.length),
      WORD_MS,
    );
    return () => clearInterval(timer);
  }, [capture_id]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    async function run(): Promise<void> {
      if (!audioPart) {
        // Photo-only capture: nothing to transcribe, pass straight through.
        onDone("", false);
        return;
      }
      // Non-blocking guarantee (§9a.2 — transcription is a trust aid, never a
      // gate): the ENTIRE leg is wrapped so that ANY throw — synchronous
      // (e.g. extFromMime on an unmapped codec mime) or async — degrades to an
      // empty transcript and STILL advances to Save instead of leaving the
      // screen stranded on "Transcribing…". That strand WAS the real "capture
      // never advances / never sends" bug: extFromMime threw on the recorder's
      // codec-qualified mime BEFORE the timeout below was even scheduled, so
      // nothing rescued it. The capture is already durably HELD (record-stop
      // pre-persist, #26), so the human always reaches Save; nothing is lost.
      try {
        const ext = extFromMime(audioPart.mime);
        const key = `media/${capture_id}/${audioPart.sha256}.${ext}`;
        // #26: race the (untimed) upload+transcribe against a hard timeout so a
        // stalled network leg can't hang the capture. On timeout, degrade to an
        // empty transcript and advance to Save (where the human can type).
        const work: Promise<TranscriptResult> = (async () => {
          try {
            // Durable upload FIRST (idempotent by sha) so the transcribe read
            // resolves the blob; a failed PUT still degrades to empty transcript.
            await putBlob(
              capture_id,
              audioPart.sha256,
              ext,
              audioPart.mime,
              audioPart.blob,
              scope,
              controller.signal,
            );
          } catch {
            // Upload failed — fall through to transcribe, which returns empty.
          }
          controller.signal.throwIfAborted();
          const transcribed = await transcriber.transcribe(
            scope,
            key,
            controller.signal,
            capture_id,
          );
          // Older /mark/transcribe responses stage the review token but do not
          // contain the governed routing result. Complete that same production
          // review through /mark/capture-review before opening Save, rather than
          // treating an absent field as a no-detection result. Newer transports
          // that already returned an exact-text detection avoid the second call.
          if (inferredRoutingSuggestionsEnabled && transcribed.transcript.trim()
              && transcribed.routing_detection === undefined
              && transcriber.stageReviewWithDetection) {
            const reviewed = await transcriber.stageReviewWithDetection(
              scope, capture_id, transcribed.transcript, controller.signal,
            );
            if (reviewed?.routing_detection?.reviewedText === transcribed.transcript) {
              return {
                ...transcribed,
                review_token: reviewed.review_token ?? transcribed.review_token,
                routing_detection: reviewed.routing_detection,
              };
            }
          }
          return transcribed;
        })();
        const timeout = new Promise<TranscriptResult>((resolve) => {
          timeoutId = setTimeout(() => {
            controller.abort();
            resolve({ transcript: "", low_confidence: false });
          }, TRANSCRIBE_TIMEOUT_MS);
        });
        const result = await Promise.race([work, timeout]);
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (cancelled) return;
        // #32: progress-only — hand the (possibly empty) transcript + low-conf
        // flag straight up and auto-advance to SaveScreen. An empty result stays
        // "" (never undefined) so SaveScreen still renders an editable field for
        // the human to type what they said; the low-conf flag also moves to Save.
        if (inferredRoutingSuggestionsEnabled && result.routing_detection !== undefined) {
          onDone(
            result.transcript,
            result.low_confidence,
            result.review_token,
            result.routing_detection,
          );
        } else if (result.review_token !== undefined) {
          // Preserve the established callback shape for hosts that staged a
          // review token but returned no routing detector result.
          onDone(result.transcript, result.low_confidence, result.review_token);
        } else {
          // Preserve the pre-BUI-273 callback shape for fail-soft/legacy
          // transcribers that do not return a staged review candidate.
          onDone(result.transcript, result.low_confidence);
        }
      } catch {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (!cancelled) onDone("", false);
      }
    }

    void run();
    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
    // capture_id identifies the session; re-running on change is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture_id]);

  // #32: the honest "Transcribing…" indicator (bound to the real async
  // round-trip, never a timer) is the ENTIRE screen. On completion the effect
  // above calls onDone and the shell advances to SaveScreen, where the
  // transcript is rendered EDITABLE — this screen never shows an editable field.
  return (
    <section data-skin-slot="SK-CAPTURE" data-testid="transcribing-screen">
      <p class="transcribing-status" data-testid="transcribing-status" aria-live="polite">
        <span class="transcribing-spinner" aria-hidden="true" />
        {/* ONE stable announcement for a screen reader. The rotating word is
            aria-hidden so a live region does not read out a new verb every
            1s — fifteen announcements of "still working" is worse than one,
            and none of them carries information the first did not. */}
        <span class="visually-hidden">Transcribing…</span>
        <span data-testid="transcribing-word" aria-hidden="true">{TRANSCRIBING_WORDS[wordIndex]}</span>
      </p>
    </section>
  );
}
